import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaPhone, FaEnvelope, FaArrowRight, FaComments } from 'react-icons/fa';

gsap.registerPlugin(ScrollTrigger);

const SectionContactCTA = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(
      '.cta-content',
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
        },
      }
    );
  }, []);

  return (
    <section ref={sectionRef} className="py-10 md:py-2 bg-white relative overflow-visible pb-16 md:pb-20">
      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="cta-content max-w-4xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-secondary/20 text-primary font-semibold px-5 py-2 rounded-full mb-8"
          >
            <FaComments className="text-secondary-600" />
            <span className="text-sm font-medium">We're here to help</span>
          </motion.div>

          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-dark mb-6">
            Need help with your{' '}
            <span className="text-gradient">application?</span>
          </h2>

          <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Our friendly team is ready to assist you with any questions about your loan application.
          </p>

          {/* Contact Cards */}
          <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto mb-10">
            <motion.a
              href="tel:0468098248"
              whileHover={{ scale: 1.03, y: -5 }}
              whileTap={{ scale: 0.98 }}
              className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-primary/30 transition-all duration-300 group shadow-sm hover:shadow-xl"
            >
              <div className="w-14 h-14 bg-secondary rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <FaPhone className="text-primary-800 text-xl" />
              </div>
              <p className="text-gray-500 text-sm mb-1">Call us at</p>
              <p className="text-dark font-bold text-xl">0468 098 248</p>
            </motion.a>

            <motion.a
              href="mailto:info@aircashfinance.com.au"
              whileHover={{ scale: 1.03, y: -5 }}
              whileTap={{ scale: 0.98 }}
              className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-primary/30 transition-all duration-300 group shadow-sm hover:shadow-xl"
            >
              <div className="w-14 h-14 bg-secondary rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <FaEnvelope className="text-primary-800 text-xl" />
              </div>
              <p className="text-gray-500 text-sm mb-1">Email us at</p>
              <p className="text-dark font-bold text-lg break-all">info@aircashfinance.com.au</p>
            </motion.a>
          </div>

          {/* CTA Button */}
          <motion.a
            href="/contact-us"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 bg-secondary hover:bg-secondary-400 text-primary-800 font-bold py-4 px-10 rounded-full transition-all duration-300 shadow-xl hover:shadow-secondary/30 group"
          >
            Contact Us
            <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
          </motion.a>

          {/* Operating Hours */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            viewport={{ once: true }}
            className="text-gray-500 text-sm mt-10 md:mt-12 mb-8 md:mb-12"
          >
            Available Monday - Friday, 9am - 5pm AEST
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default SectionContactCTA;
